import AbstractFilterView, { type AbstractFilterViewArgs } from '@UI/Helpers/Filters/AbstractFilterView';
import resolvePeriodBounds from '@UI/Pages/WorkflowRuns/Filters/Helpers/resolvePeriodBounds';

export type WorkflowRunPeriod = 'all' | 'today' | 'last7Days' | 'last30Days' | 'custom';

export type WorkflowRunFilterViewArgs = AbstractFilterViewArgs & {
	readonly facilityId?: number | null;
	readonly userId?: number | null;
	readonly status?: string;
	readonly kind?: string | null;
	readonly period?: WorkflowRunPeriod;
	readonly startedFrom?: Date | null;
	readonly startedTo?: Date | null;
	readonly beneficiaryId?: number | null;
	readonly assignedUserId?: number | null;
};

const DEFAULT_STATUS = 'all';
const DEFAULT_PERIOD: WorkflowRunPeriod = 'all';

/**
 * Carries the scope it was resolved for (`facilityId`, `userId`), so a stale signal value can be
 * told apart from one that matches the current agency and caller (`belongsTo`). `queryParams` never
 * reads the clock: every date it emits was already resolved and stored by `setPeriod` or
 * `setCustomRange`, so two reads of the same instance always produce the same query string.
 */
class WorkflowRunFilterView extends AbstractFilterView<WorkflowRunFilterViewArgs> {
	readonly facilityId: number | null;
	readonly userId: number | null;
	readonly status: string;
	readonly kind: string | null;
	readonly period: WorkflowRunPeriod;
	readonly startedFrom: Date | null;
	readonly startedTo: Date | null;
	readonly beneficiaryId: number | null;
	readonly assignedUserId: number | null;

	constructor(args: WorkflowRunFilterViewArgs) {
		super(args);

		this.facilityId = args.facilityId ?? null;
		this.userId = args.userId ?? null;
		this.status = args.status ?? DEFAULT_STATUS;
		this.kind = args.kind ?? null;
		this.period = args.period ?? DEFAULT_PERIOD;
		this.startedFrom = args.startedFrom ?? null;
		this.startedTo = args.startedTo ?? null;
		this.beneficiaryId = args.beneficiaryId ?? null;
		this.assignedUserId = args.assignedUserId ?? null;

		Object.freeze(this);
	}

	static createDefault(facilityId: number | null, userId: number | null): WorkflowRunFilterView {
		return new WorkflowRunFilterView({ facilityId, userId });
	}

	/** `false` as soon as either stored scope field is `null`, including the signal's own initial value. */
	belongsTo(facilityId: number, userId: number): boolean {
		return this.facilityId === facilityId && this.userId === userId;
	}

	/** Number of criteria that narrow the results; sorting never counts. */
	get activeFilterCount(): number {
		return [
			this.status !== DEFAULT_STATUS,
			this.kind !== null,
			this.period !== DEFAULT_PERIOD,
			this.beneficiaryId !== null,
			this.assignedUserId !== null,
		].filter(Boolean).length;
	}

	override get queryParams(): URLSearchParams {
		const params = super.queryParams;

		params.set('status', this.status);

		if (this.kind !== null) {
			params.set('kind', this.kind);
		}

		if (this.beneficiaryId !== null) {
			params.set('beneficiaryId', String(this.beneficiaryId));
		}

		if (this.assignedUserId !== null) {
			params.set('assignedUserId', String(this.assignedUserId));
		}

		// Emitted only once both bounds are stored: "Toute période" and an incomplete free range both
		// omit it, since a single bound would turn the server's `400` into a flash of error.
		if (this.startedFrom !== null && this.startedTo !== null) {
			const [lowerBound, upperBound] =
				this.startedFrom.getTime() <= this.startedTo.getTime()
					? [this.startedFrom, this.startedTo]
					: [this.startedTo, this.startedFrom];

			params.append('startedBetween', lowerBound.toISOString());
			params.append('startedBetween', upperBound.toISOString());
		}

		return params;
	}

	setStatus(status: string): this {
		return this.cloneWith({ status, page: 0 });
	}

	setKind(kind: string | null): this {
		return this.cloneWith({ kind, page: 0 });
	}

	/**
	 * `custom` keeps whatever free range is already stored: the "Dates libres" option only switches
	 * the display mode to the range calendar, `setCustomRange` is what writes the actual bounds.
	 */
	setPeriod(period: WorkflowRunPeriod, now: Date): this {
		if (period === 'custom') {
			return this.cloneWith({ period, page: 0 });
		}

		const bounds = resolvePeriodBounds(period, now);
		return this.cloneWith({
			period,
			startedFrom: bounds.startedFrom,
			startedTo: bounds.startedTo,
			page: 0,
		});
	}

	/**
	 * The upper bound is carried to the end of its day, same rule as every preset: reusing
	 * `resolvePeriodBounds('today', to).startedTo` gets it without a second implementation of that
	 * math here.
	 */
	setCustomRange(from: Date | null, to: Date | null): this {
		return this.cloneWith({
			period: 'custom',
			startedFrom: from,
			startedTo: to === null ? null : resolvePeriodBounds('today', to).startedTo,
			page: 0,
		});
	}

	setBeneficiaryId(beneficiaryId: number | null): this {
		return this.cloneWith({ beneficiaryId, page: 0 });
	}

	setAssignedUserId(assignedUserId: number | null): this {
		return this.cloneWith({ assignedUserId, page: 0 });
	}
}

export default WorkflowRunFilterView;
